import { useState, useEffect } from 'react'
import { AdminLayout } from '../../components/layouts/Layout'
import { DataTable } from '../../components/common/DataTable'
import { CRUDModal } from '../../components/common/CRUDModal'
import { Badge, Button } from '../../components/common/UI'
import { UserPlus, Edit, Trash2 } from 'lucide-react'
import { formatters } from '../../utils/formatting'
import { adminService } from '../../services/api'

export default function AdminManagement() {
  const [admins, setAdmins] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingAdmin, setEditingAdmin] = useState<any>(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const data = await adminService.list()
      setAdmins(data)
    } catch (error) {
      console.error('Failed to load admins:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async (data: any) => {
    await adminService.create({ ...data, status: 'ACTIVE' })
    await loadData()
  }

  const handleUpdate = async (data: any) => {
    if (editingAdmin) {
      await adminService.update(editingAdmin.id, data)
      await loadData()
    }
  }

  const handleDelete = async (row: any) => {
    await adminService.delete(row.id)
    await loadData()
  }

  const fields = [
    { name: 'firstName', label: 'First Name', type: 'text' as const, required: true },
    { name: 'lastName', label: 'Last Name', type: 'text' as const, required: true },
    { name: 'email', label: 'Email', type: 'email' as const, required: true },
    {
      name: 'role',
      label: 'Role',
      type: 'select' as const,
      options: [
        { value: 'ADMIN', label: 'Admin' },
        { value: 'MODERATOR', label: 'Moderator' },
        { value: 'SUPER_ADMIN', label: 'Super Admin' }
      ],
      required: true
    }
  ]

  const columns = [
    {
      key: 'firstName' as const,
      label: 'Name',
      render: (_: any, row: any) => `${row.firstName} ${row.lastName}`
    },
    { key: 'email' as const, label: 'Email' },
    {
      key: 'role' as const,
      label: 'Role',
      render: (value: string) => <Badge variant="info">{value}</Badge>
    },
    {
      key: 'status' as const,
      label: 'Status',
      render: (value: string) => (
        <Badge variant={value === 'ACTIVE' ? 'success' : 'default'}>{value}</Badge>
      )
    },
    {
      key: 'createdAt' as const,
      label: 'Created',
      render: (value: string) => formatters.date(value)
    }
  ]

  const actions = [
    {
      icon: Edit,
      label: 'Edit',
      onClick: (row: any) => {
        setEditingAdmin(row)
        setShowModal(true)
      }
    },
    {
      icon: Trash2,
      label: 'Delete',
      onClick: handleDelete,
      variant: 'ghost' as const
    }
  ]

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p>Loading...</p>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Admin Management</h1>
            <p className="text-gray-600">Manage admin users</p>
          </div>
          <Button onClick={() => setShowModal(true)} icon={UserPlus}>
            Add Admin
          </Button>
        </div>

        <DataTable data={admins} columns={columns} actions={actions} />

        <CRUDModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false)
            setEditingAdmin(null)
          }}
          onSubmit={async (data) => {
            if (editingAdmin) {
              await handleUpdate(data)
            } else {
              await handleCreate(data)
            }
            setShowModal(false)
            setEditingAdmin(null)
          }}
          title={editingAdmin ? 'Edit Admin' : 'Add Admin'}
          fields={fields}
          initialData={editingAdmin || undefined}
        />
      </div>
    </AdminLayout>
  )
}